import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Loading } from '@components/atoms/Loading';
import { Button } from '@components/atoms/Button';
import { StockOverviewDashboard } from '@components/organisms/StockOverviewDashboard';
import { ReceiveStockModal } from '@components/organisms/ReceiveStockModal';
import { AdjustStockModal } from '@components/organisms/AdjustStockModal';
import { WarehouseGeofenceModal } from '@components/organisms/WarehouseGeofenceModal';
import { WarehouseLayout } from '@components/layout/WarehouseLayout';
import { useWarehouseContext } from '@components/layout/WarehouseContext';
import {
  useStockLevels,
  useLowStockItems,
  useMaterialCategories,
  useStockTransactions,
} from '@hooks/api/warehouse';
import { generateStockOverviewPdf } from '@utils/stockOverviewPdf';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PinDropOutlinedIcon from '@mui/icons-material/PinDropOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

const WarehousePageContent: React.FC = () => {
  const history = useHistory();
  const { selectedWarehouse, isLoading: warehousesLoading } = useWarehouseContext();
  const [isReceiveModalOpen, setIsReceiveModalOpen] = React.useState(false);
  const [isAdjustModalOpen, setIsAdjustModalOpen] = React.useState(false);
  const [isGeofenceModalOpen, setIsGeofenceModalOpen] = React.useState(false);

  // API Hooks - filtered by selected warehouse from context
  const {
    data: stockLevels = [],
    isLoading: stockLevelsLoading,
    isError: stockLevelsError,
    refetch: refetchStockLevels,
  } = useStockLevels(selectedWarehouse?.warehouseId);
  const {
    data: lowStockItems = [],
    isLoading: lowStockLoading,
    isError: lowStockError,
    refetch: refetchLowStock,
  } = useLowStockItems();
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isError: categoriesError,
    refetch: refetchCategories,
  } = useMaterialCategories();
  const {
    data: transactions = [],
    isLoading: transactionsLoading,
    isError: transactionsError,
    refetch: refetchTransactions,
  } = useStockTransactions({
    warehouseId: selectedWarehouse?.warehouseId,
  });

  // Consider errors as "done loading" to not block the UI
  const isLoading =
    warehousesLoading ||
    (stockLevelsLoading && !stockLevelsError) ||
    (lowStockLoading && !lowStockError) ||
    (categoriesLoading && !categoriesError) ||
    (transactionsLoading && !transactionsError);

  const handleRefresh = useCallback(() => {
    refetchStockLevels();
    refetchLowStock();
    refetchCategories();
    refetchTransactions();
  }, [refetchStockLevels, refetchLowStock, refetchCategories, refetchTransactions]);

  const handleReceiveStock = useCallback(() => {
    setIsReceiveModalOpen(true);
  }, []);

  const handleCloseReceiveModal = useCallback(() => {
    setIsReceiveModalOpen(false);
  }, []);

  const handleReceiveSuccess = useCallback(() => {
    refetchStockLevels();
    refetchLowStock();
    refetchTransactions();
  }, [refetchStockLevels, refetchLowStock, refetchTransactions]);

  const handleAdjustStock = useCallback(() => {
    setIsAdjustModalOpen(true);
  }, []);

  const handleCloseAdjustModal = useCallback(() => {
    setIsAdjustModalOpen(false);
  }, []);

  const handleAdjustSuccess = useCallback(() => {
    refetchStockLevels();
    refetchLowStock();
    refetchTransactions();
  }, [refetchStockLevels, refetchLowStock, refetchTransactions]);

  const handleViewAllTransactions = useCallback(() => {
    history.push('/warehouse/transactions');
  }, [history]);

  const handleViewAllAlerts = useCallback(() => {
    history.push('/warehouse/alerts');
  }, [history]);

  const handleExportReport = useCallback(() => {
    generateStockOverviewPdf({
      stockLevels,
      categories,
      warehouseName: selectedWarehouse?.name,
    });
  }, [stockLevels, categories, selectedWarehouse]);

  if (warehousesLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loading size="3xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Stock Overview
        </h1>

        <div className="flex gap-2">
          <Button
            variant="secondary"
            leftIcon={<PinDropOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setIsGeofenceModalOpen(true)}
            disabled={!selectedWarehouse}
          >
            Geofence
          </Button>
          <Button
            variant="secondary"
            leftIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
            onClick={handleExportReport}
          >
            Export PDF
          </Button>
          <Button
            variant="primary"
            leftIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleReceiveStock}
          >
            Receive Stock
          </Button>
          <Button
            variant="secondary"
            onClick={handleAdjustStock}
          >
            Adjust Stock
          </Button>
          <Button variant="tertiary" onClick={handleRefresh}>
            <RefreshIcon sx={{ fontSize: 16 }} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <StockOverviewDashboard
        stockLevels={stockLevels}
        lowStockItems={lowStockItems}
        recentTransactions={transactions}
        categories={categories}
        isLoading={isLoading}
        onViewAllTransactions={handleViewAllTransactions}
        onViewAllAlerts={handleViewAllAlerts}
        onReceiveStock={handleReceiveStock}
      />

      {/* Receive Stock Modal */}
      <ReceiveStockModal
        isOpen={isReceiveModalOpen}
        onClose={handleCloseReceiveModal}
        onSuccess={handleReceiveSuccess}
      />

      {/* Adjust Stock Modal */}
      <AdjustStockModal
        isOpen={isAdjustModalOpen}
        onClose={handleCloseAdjustModal}
        onSuccess={handleAdjustSuccess}
      />

      {/* Warehouse Geofence Modal */}
      <WarehouseGeofenceModal
        isOpen={isGeofenceModalOpen}
        onClose={() => setIsGeofenceModalOpen(false)}
        warehouse={selectedWarehouse ?? null}
        hideActiveToggle
      />
    </div>
  );
};

const WarehousePage: React.FC = () => {
  return (
    <WarehouseLayout>
      <WarehousePageContent />
    </WarehouseLayout>
  );
};

export default WarehousePage;
